// /proyectos/:id/archivo — las observaciones terminadas del proyecto.
//
// Solo lectura, y a propósito: acá no hay acciones porque no queda
// ninguna por tomar. Dos poblaciones distintas caen en el archivo por
// caminos distintos —las que el propietario cerró dando su conformidad
// (overlay de cierre, ADR-0128: el cierre NO mueve `observation_status`)
// y las que la inmobiliaria rechazó (un estado terminal de la receta)—
// así que el predicado tiene que mirar las dos cosas. Mirar solo el
// estado dejaría fuera todo lo cerrado; mirar solo el overlay dejaría
// fuera todo lo rechazado.
//
// Una rechazada no se reabre: si el propietario insiste, levanta una
// observación nueva. El pie de la tabla lo dice.
//
// Misma tabla compartida que el listado (`ObservacionesTable`, handoff
// listado §Alcance), sin selección ni KPIs: filtros por columna con el
// alcance «cerradas» forzado, búsqueda y paginación, todo en la URL.
import { useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  fechaApoyoPropietario,
  type ObservationClosedReason,
  type PostventaObservation,
} from "@bitacorapro/api-types";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { ObservacionesFiltros } from "../components/ObservacionesFiltros";
import { ObservacionesTable, type ObsColumn } from "../components/ObservacionesTable";
import { ObservacionPills } from "../components/ObservacionPills";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Pager } from "../components/Pager";
import { SevPill } from "../components/SevPill";
import { Skel } from "../components/Skel";
import { ApiError } from "../lib/api/error";
import { useCarteraProyectoDetail, usePostventaObservations, useRecipe } from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import {
  applyFilters,
  BUCKET_ESTADOS,
  buildFilas,
  clampPage,
  CLEAR_FILTERS,
  hasActiveFilters,
  normalizePagingParams,
  PAGE_SIZES,
  pageSlice,
  parseListState,
  totalPages,
  writeListState,
  type FilterKey,
  type ListPatch,
  type PageSize,
} from "../lib/postventa/observacionesList";
import type { PresentationalState } from "../lib/postventa/phase";
import { fmtDate } from "../lib/util";

const ARCHIVO_FILTROS: readonly FilterKey[] = [
  "estado",
  "criticidad",
  "unidad",
  "contratista",
  "especialidad",
];

/** Cómo terminó. Sale del estado presentacional (fase del engine +
 *  overlay de cierre): «Cerrada» es el cierre del propietario, «Cerrada
 *  sin respuesta» el automático (ADR-0142), «Rechazada» el
 *  terminal de la inmobiliaria. Ninguna
 *  clave de estado se compara acá (fitness
 *  observation-phase-single-source). */
function resolucion(o: PostventaObservation, state: PresentationalState): string {
  // ADR-0142 — nunca una conformidad: su propio kind y su propia palabra.
  // Mientras el servidor mande `reopenableUntil`, el propietario todavía
  // puede reabrirla con un toque: el jefe lo ve acá.
  if (state.kind === "closed_no_response") {
    const base = `Cerrada sin respuesta · ciclo ${state.ciclo}`;
    return o.reopenableUntil
      ? `${base} · reabrible hasta el ${fechaApoyoPropietario(o.reopenableUntil)}`
      : base;
  }
  if (state.kind === "conforme") {
    // «migrated» (ADR-0134 §7): cerrada al migrar un seguimiento previo,
    // sin conformidad de nadie — nunca se cuenta como una.
    const reason: ObservationClosedReason | null | undefined = o.observationClosedReason;
    const quien =
      reason === "migrated"
        ? "Cerrada al migrar el seguimiento anterior"
        : "Conformidad del propietario";
    return `${quien} · ciclo ${state.ciclo}`;
  }
  // ADR-0141: el cierre del carril del rechazo. Cómo se llegó (el
  // propietario aceptó, el jefe mantuvo tras la apelación, o el cierre
  // administrativo de la migración) vive en el hilo; la columna resume.
  return "Rechazo cerrado · sin cobertura de garantía";
}

export function ArchivoProyecto() {
  const { id } = useParams<{ id: string }>();
  const { data: recipe } = useRecipe();
  const proyecto = useCarteraProyectoDetail(id, { limit: 1 });
  const observations = usePostventaObservations(id);
  const view = useQueryView(observations);
  const data = view.kind === "ready" ? view.data : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseListState(searchParams), [searchParams]);

  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";
  const nombre = proyecto.data?.group.name ?? null;

  // Terminadas = el grupo «cerradas» del estado presentacional: cubre el
  // overlay de conformidad y el terminal de rechazo en un solo predicado.
  const now = useMemo(() => new Date(), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const terminales = useMemo(
    () =>
      buildFilas(data?.observations ?? [], recipe, now).filter(
        (f) => f.state.bucket === "cerradas"
      ),
    [data, recipe, now]
  );
  const filtered = useMemo(() => applyFilters(terminales, state), [terminales, state]);
  const pagina = clampPage(state.pagina, filtered.length, state.tam);
  const pages = totalPages(filtered.length, state.tam);

  function update(patch: ListPatch) {
    setSearchParams((prev) => writeListState(prev, patch), { replace: true });
  }

  // `?pagina=` / `?tam=` crudos que no son los que se pintan (basura,
  // fuera de rango) se corrigen en la URL con replace.
  const listo = view.kind === "ready";
  useEffect(() => {
    if (!listo) return;
    const canon = normalizePagingParams(searchParams, pagina);
    if (canon) setSearchParams(canon, { replace: true });
  }, [listo, pagina, searchParams, setSearchParams]);

  const columns: ObsColumn[] = [
    { key: "unidad", header: "Unidad", className: "col-num col-unit", cell: (f) => f.unidad },
    { key: "criticidad", header: "Criticidad", cell: (f) => <SevPill code={f.sev} /> },
    {
      key: "estado",
      header: "Estado",
      className: "col-estado",
      cell: (f) => (
        <span className="obs-pills">
          <ObservacionPills part="head" state={f.state} />
        </span>
      ),
    },
    {
      key: "resolucion",
      header: "Resolución",
      className: "col-wrap cell-meta",
      cell: (f) => resolucion(f.o, f.state),
    },
    {
      key: "contratista",
      header: "Contratista",
      className: "col-wrap cell-meta",
      cell: (f) => f.contratista ?? "—",
    },
    {
      key: "fecha",
      header: "Fecha",
      className: "cell-meta",
      cell: (f) => fmtDate(f.o.observationClosedAt ?? f.o.observationStatusAt ?? f.o.createdAt),
    },
  ];

  if (view.kind === "error" && view.error instanceof ApiError && view.error.status === 404) {
    return (
      <div className="page-pad">
        <h1 className="page-header__title">{groupLabel} no encontrado</h1>
        <Link to="/proyectos" className="btn btn--secondary">
          <Icon.Arrow s={14} /> Volver a {groupPlural.toLowerCase()}
        </Link>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            <Link to="/proyectos">{groupPlural}</Link> /{" "}
            {nombre === null && proyecto.isPending && proyecto.fetchStatus === "fetching" ? (
              // .skel is a block: inside this run of links it would break
              // the eyebrow into three lines. The inline-block holder keeps
              // it on the real line.
              <span style={{ display: "inline-block", width: 120, verticalAlign: "top" }}>
                <Skel variant="text" width="100%" />
              </span>
            ) : (
              <Link to={`/proyectos/${id}`}>{nombre ?? groupLabel}</Link>
            )}{" "}
            / Archivo
          </div>
          <h1 className="page-header__title">Archivo</h1>
          <p className="page-header__sub">
            Observaciones terminadas: cerradas con la conformidad del propietario y rechazadas.
          </p>
        </div>
        <div className="page-header__actions">
          <Link to={`/proyectos/${id}`} className="btn btn--secondary btn--sm">
            <Icon.Arrow s={14} /> Volver al {groupLabel.toLowerCase()}
          </Link>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Terminadas</div>
          {listo && <div className="panel__sub mono">{terminales.length}</div>}
        </div>
        {listo && terminales.length > 0 && (
          <ObservacionesFiltros
            filas={terminales}
            state={state}
            keys={ARCHIVO_FILTROS}
            domains={{ estado: BUCKET_ESTADOS.cerradas }}
            onPatch={update}
            resultCount={filtered.length}
            searchPlaceholder="Buscar observación, recinto o unidad…"
          />
        )}
        {view.kind === "loading" ? (
          <>
            <PageSkeleton label="Cargando el archivo…" visible={view.visible}>
              <ArchivoSkeleton columns={columns} />
            </PageSkeleton>
            {view.slow && (
              <div className="notice-banner notice-banner--action">
                <span>
                  <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
                  intentando.
                </span>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => void observations.refetch()}
                >
                  Reintentar
                </button>
              </div>
            )}
          </>
        ) : view.kind === "error" ? (
          <div className="panel__body--flush">
            {view.offline ? (
              <PageError
                title="Sin conexión"
                description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                onRetry={view.retry}
              />
            ) : (
              <PageError
                title="No pudimos cargar el archivo"
                description="No se perdió nada: vuelve a intentarlo."
                onRetry={view.retry}
              />
            )}
          </div>
        ) : view.kind !== "ready" ? (
          // `disabled` (no id en la URL) o `empty`: nada viene en camino,
          // nunca un skeleton eterno.
          <div className="panel__body--flush">
            <EmptyState title="El archivo está vacío" />
          </div>
        ) : terminales.length === 0 ? (
          <div className="panel__body--flush">
            <div className="empty">
              <div className="empty__title">El archivo está vacío</div>
              <div className="empty__sub">
                Aquí van a quedar las observaciones que el propietario cierre dando su conformidad y
                las que ustedes rechacen.
              </div>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="panel__body--flush">
            <div className="empty">
              <div className="empty__title">Ninguna observación coincide con los filtros</div>
              {hasActiveFilters(state) && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => update(CLEAR_FILTERS)}
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {view.refreshFailed && (
              <div className="notice-banner notice-banner--action">
                <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => void observations.refetch()}
                >
                  Reintentar
                </button>
              </div>
            )}
            <ObservacionesTable
              filas={pageSlice(filtered, pagina, state.tam)}
              columns={columns}
              caption={`Observaciones terminadas del ${groupLabel.toLowerCase()}`}
              rowHref={(f) => `/proyectos/${id}/observaciones/${f.o.id}`}
            />
            <Pager
              numbered
              currentPage={pagina}
              totalPages={pages}
              totalItems={filtered.length}
              pageSize={state.tam}
              onPage={(n) => update({ pagina: n })}
              pageSizes={PAGE_SIZES}
              onPageSize={(n) => update({ tam: n as PageSize })}
            />
            <div className="pager">
              <span className="pager__meta">
                Una observación rechazada no se reabre: si el propietario insiste, levanta una
                nueva.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// La misma tabla compartida (`ObservacionesTable`: scroll, `.dtable`
// compacta sin selección, título + columnas + chevron) con sus encabezados
// reales y filas en blanco: diez filas compactas llenan 1280×800 bajo el
// encabezado y la cabecera del panel.
function ArchivoSkeleton({ columns }: { columns: readonly ObsColumn[] }) {
  return (
    <div className="dtable-scroll dtable-scroll--obs">
      <table className="dtable dtable--compact dtable--rowlink dtable--no-check">
        <thead>
          <tr>
            <th scope="col" className="col-title">
              Observación
            </th>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.className}>
                {c.header}
              </th>
            ))}
            <th scope="col" className="col-chev">
              <span className="sr-only">Abrir</span>
            </th>
          </tr>
        </thead>
        <tbody aria-hidden="true">
          {Array.from({ length: 10 }, (_, i) => (
            <tr key={i}>
              <td>
                <div className="skel-stack">
                  <Skel width="55%" height={12} />
                  <Skel width="35%" height={9} />
                </div>
              </td>
              <td>
                <Skel width={36} height={12} />
              </td>
              <td>
                <Skel width={72} height={20} pill />
              </td>
              <td>
                <Skel width={88} height={20} pill />
              </td>
              <td>
                <Skel width={150} height={12} />
              </td>
              <td>
                <Skel width={90} height={12} />
              </td>
              <td>
                <Skel width={70} height={12} />
              </td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
